// ============================================================
// TMR Portal Admin — as decisões do vínculo com o legado (05 I7)
// ============================================================
import type { LegacyLinkOutcome, LegacyUserCandidate } from './contract';

/**
 * Below this many characters the server answers an empty list, so asking is a wasted round-trip. Mirrors
 * `SearchLegacyUsersHandler.MinimumSearchLength`: neither `nome` nor `login` is indexed in the legacy, so
 * every term there is a sequential scan.
 */
export const MIN_LEGACY_SEARCH_LENGTH = 3;

/**
 * Why the account was not correlated to the legacy, in the operator's terms.
 *
 * `Linked` is absent on purpose — a successful correlation needs no explanation, and the row already shows
 * it. These four are the cases where somebody has to go and pick a login by hand, and saying which one it
 * was is the whole point: the consequence of not linking is invisible otherwise (an account named Gestor
 * da Conta replicates `produtores.gestor_conta` as null, with no error anywhere).
 */
const REASONS: Record<Exclude<LegacyLinkOutcome, 'Linked'>, string> = {
  NotFound: 'Não encontramos ninguém com este e-mail no legado.',
  Ambiguous: 'Há mais de um cadastro com este e-mail no legado, então não dá para escolher sozinho.',
  AlreadyTaken: 'O cadastro do legado com este e-mail já pertence a outro usuário.',
  Unavailable: 'Não foi possível consultar o legado agora.',
};

/** The sentence for a failed correlation; null when it succeeded and there is nothing to explain. */
export function legacyLinkReason(outcome: LegacyLinkOutcome): string | null {
  return outcome === 'Linked' ? null : REASONS[outcome];
}

/**
 * Whether a single result is the account's own e-mail match — the automatic correlation the creation would
 * have made, found for the operator instead of by them.
 *
 * Worth marking, because it is what lets somebody click without checking the row field by field. Two
 * results are never a match: the legacy has no unique index on e-mail, and that ambiguity is exactly what
 * the server refuses to resolve on its own.
 */
export function isEmailMatch(term: string, accountEmail: string, resultCount: number): boolean {
  return resultCount === 1 && term.trim().toLowerCase() === accountEmail.trim().toLowerCase();
}

/**
 * What the operator can do with one candidate row.
 *
 * - `current` — this is the account's own link; the row confirms it rather than offering it again.
 * - `takenByOther` — another account holds it. The correlation is unique, so offering it would produce
 *   only a 409; greying it out is what keeps the picker honest.
 * - `blocked` — the account already points somewhere else. Replacing a correlation is an unlink first.
 * - `available` — linkable.
 */
export type CandidateState = 'current' | 'takenByOther' | 'blocked' | 'available';

export function candidateState(
  candidate: LegacyUserCandidate,
  currentLegacyUserId: number | null,
): CandidateState {
  if (candidate.legacyUserId === currentLegacyUserId) return 'current';
  if (candidate.alreadyLinked) return 'takenByOther';
  return currentLegacyUserId === null ? 'available' : 'blocked';
}

/**
 * Who holds a candidate the operator cannot take, phrased for the row.
 *
 * An owner outside the operator's scope exists and cannot be named (06 §6) — the row still has to say the
 * id is spoken for, or it reads as an unexplained disabled button.
 */
export function takenByLabel(candidate: LegacyUserCandidate): string {
  return candidate.linkedToUserName
    ? `Já vinculado a ${candidate.linkedToUserName}.`
    : 'Já vinculado a outra conta.';
}
